// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------

/**
 * ShuiEvent event class for our CustomEvent replacement to work with the shui/check-events eslint rule
 */
export class ShuiEvent<Prop extends string, TDetail = void> extends CustomEvent<TDetail> {
  override readonly type!: `${Prop}`;

  constructor(prop: Prop, init?: CustomEventInit<TDetail>) {
    super(`${prop}`, init);
  }
}
/**
 * All the non-legacy events ordered alphabetically
 */
export class ActiveChangedEvent extends ShuiEvent<'active-changed'> {
  constructor(init?: CustomEventInit) {
    super('active-changed', init);
  }
}

export class ApplyFirstSelectionEvent extends ShuiEvent<'apply-first-selection'> {
  constructor(init?: CustomEventInit) {
    super('apply-first-selection', init);
  }
}

export class ArrowPartClickEvent extends ShuiEvent<'arrow-part-click'> {
  constructor(init?: CustomEventInit) {
    super('arrow-part-click', init);
  }
}

export class ButtonPartClickEvent extends ShuiEvent<'button-part-click'> {
  constructor(init?: CustomEventInit) {
    super('button-part-click', init);
  }
}

export class CalendarClosedEvent extends ShuiEvent<'calendar-closed'> {
  constructor(init?: CustomEventInit) {
    super('calendar-closed', init);
  }
}

export class CheckedEvent extends ShuiEvent<'checked'> {
  constructor(init?: CustomEventInit) {
    super('checked', init);
  }
}

export class ClearSearchEvent extends ShuiEvent<'clearsearch'> {
  constructor(init?: CustomEventInit) {
    super('clearsearch', init);
  }
}

export class ClearClickedEvent extends ShuiEvent<'clear-clicked'> {
  constructor(init?: CustomEventInit) {
    super('clear-clicked', init);
  }
}

export class ClickedEvent extends ShuiEvent<'clicked'> {
  constructor(init?: CustomEventInit) {
    super('clicked', init);
  }
}

export class ClickedPageMoveEvent extends ShuiEvent<'clicked-page-move'> {
  constructor(init?: CustomEventInit) {
    super('clicked-page-move', init);
  }
}

export class CloseEvent extends ShuiEvent<'close'> {
  constructor(init?: CustomEventInit) {
    super('close', init);
  }
}

export class ClosePressedEvent extends ShuiEvent<'close-pressed'> {
  constructor(init?: CustomEventInit) {
    super('close-pressed', init);
  }
}

export class ClosedEvent extends ShuiEvent<'closed'> {
  constructor(init?: CustomEventInit) {
    super('closed', init);
  }
}

export class ClosingEvent extends ShuiEvent<'closing'> {
  constructor(init?: CustomEventInit) {
    super('closing', init);
  }
}

export class CollapseEvent extends ShuiEvent<'collapse'> {
  constructor(init?: CustomEventInit) {
    super('collapse', init);
  }
}

export class ControlValueChangeEvent extends ShuiEvent<'control-value-change'> {
  constructor(init?: CustomEventInit) {
    super('control-value-change', init);
  }
}

export class DrawerClosedEvent extends ShuiEvent<'drawer-closed'> {
  constructor(init?: CustomEventInit) {
    super('drawer-closed', init);
  }
}

export class ExpandEvent extends ShuiEvent<'expand'> {
  constructor(init?: CustomEventInit) {
    super('expand', init);
  }
}

export class FullscreenToggleEvent extends ShuiEvent<'fullscreen-toggle'> {
  constructor(init?: CustomEventInit) {
    super('fullscreen-toggle', init);
  }
}

export class HeadReadyEvent extends ShuiEvent<'head-ready'> {
  constructor(init?: CustomEventInit) {
    super('head-ready', init);
  }
}

export class HintClickedEvent extends ShuiEvent<'hint-clicked'> {
  constructor(init?: CustomEventInit) {
    super('hint-clicked', init);
  }
}

export class InputClickedEvent extends ShuiEvent<'input-clicked'> {
  constructor(init?: CustomEventInit) {
    super('input-clicked', init);
  }
}

export class InputFocusedEvent extends ShuiEvent<'input-focused'> {
  constructor(init?: CustomEventInit) {
    super('input-focused', init);
  }
}

export class InputHoverEvent extends ShuiEvent<'input-hover'> {
  constructor(init?: CustomEventInit) {
    super('input-hover', init);
  }
}

export class ItemActiveEvent extends ShuiEvent<'item-active'> {
  constructor(init?: CustomEventInit) {
    super('item-active', init);
  }
}

export class KeypadClosedEvent extends ShuiEvent<'keypad-closed'> {
  constructor(init?: CustomEventInit) {
    super('keypad-closed', init);
  }
}

export class KeypadOpenedEvent extends ShuiEvent<'keypad-opened'> {
  constructor(init?: CustomEventInit) {
    super('keypad-opened', init);
  }
}

export class KeyPressEvent extends ShuiEvent<'key-press'> {
  constructor(init?: CustomEventInit) {
    super('key-press', init);
  }
}

export class LabelHoverEvent extends ShuiEvent<'label-hover'> {
  constructor(init?: CustomEventInit) {
    super('label-hover', init);
  }
}

export class LeftArrowClickEvent extends ShuiEvent<'left-arrow-click'> {
  constructor(init?: CustomEventInit) {
    super('left-arrow-click', init);
  }
}

export class LogoClickedEvent extends ShuiEvent<'logo-clicked'> {
  constructor(init?: CustomEventInit) {
    super('logo-clicked', init);
  }
}

export class MaximizePressedEvent extends ShuiEvent<'maximize-pressed'> {
  constructor(init?: CustomEventInit) {
    super('maximize-pressed', init);
  }
}

export class MenuClickedEvent extends ShuiEvent<'menu-clicked'> {
  constructor(init?: CustomEventInit) {
    super('menu-clicked', init);
  }
}

export class MenuItemActiveEvent extends ShuiEvent<'menu-item-active'> {
  constructor(init?: CustomEventInit) {
    super('menu-item-active', init);
  }
}

export class MiddleScrolledEvent extends ShuiEvent<'middle-scrolled'> {
  constructor(init?: CustomEventInit) {
    super('middle-scrolled', init);
  }
}

export class MinimizePressedEvent extends ShuiEvent<'minimize-pressed'> {
  constructor(init?: CustomEventInit) {
    super('minimize-pressed', init);
  }
}

// does not follow kebab-case but needs to be there for backwards compatibility
export class MinusPressedEvent extends ShuiEvent<'minusPressed'> {
  constructor(init?: CustomEventInit) {
    super('minusPressed', init);
  }
}
// does not follow kebab-case but needs to be there for backwards compatibility
export class PlusPressedEvent extends ShuiEvent<'plusPressed'> {
  constructor(init?: CustomEventInit) {
    super('plusPressed', init);
  }
}

export class ModalClosedEvent extends ShuiEvent<'modal-closed'> {
  constructor(init?: CustomEventInit) {
    super('modal-closed', init);
  }
}

export class MonthClickedEvent extends ShuiEvent<'month-clicked'> {
  constructor(init?: CustomEventInit) {
    super('month-clicked', init);
  }
}

export class MonthHeaderClickedEvent extends ShuiEvent<'month-header-clicked'> {
  constructor(init?: CustomEventInit) {
    super('month-header-clicked', init);
  }
}

export class NextPageMoveEvent extends ShuiEvent<'next-page-move'> {
  constructor(init?: CustomEventInit) {
    super('next-page-move', init);
  }
}

export class NoresultEvent extends ShuiEvent<'noresult'> {
  constructor(init?: CustomEventInit) {
    super('noresult', init);
  }
}

export class OverlayClosedEvent extends ShuiEvent<'overlay-closed'> {
  constructor(init?: CustomEventInit) {
    super('overlay-closed', init);
  }
}

export class PreviousPageMoveEvent extends ShuiEvent<'previous-page-move'> {
  constructor(init?: CustomEventInit) {
    super('previous-page-move', init);
  }
}

export class ReadyToUpdateValueEvent extends ShuiEvent<'ready-to-update-value'> {
  constructor(init?: CustomEventInit) {
    super('ready-to-update-value', init);
  }
}

export class RightArrowClickEvent extends ShuiEvent<'right-arrow-click'> {
  constructor(init?: CustomEventInit) {
    super('right-arrow-click', init);
  }
}

export class ProgressChangedEvent extends ShuiEvent<'progress-changed'> {
  constructor(init?: CustomEventInit) {
    super('progress-changed', init);
  }
}

export class ScrollChangedEvent extends ShuiEvent<'scroll-changed'> {
  constructor(init?: CustomEventInit) {
    super('scroll-changed', init);
  }
}

export class ScrollEndEvent extends ShuiEvent<'scroll-end'> {
  constructor(init?: CustomEventInit) {
    super('scroll-end', init);
  }
}

export class TableScrollEvent extends ShuiEvent<'table-scroll'> {
  constructor(init?: CustomEventInit) {
    super('table-scroll', init);
  }
}

export class ScrollpickerClosedEvent extends ShuiEvent<'scrollpicker-closed'> {
  constructor(init?: CustomEventInit) {
    super('scrollpicker-closed', init);
  }
}

export class SearchEvent extends ShuiEvent<'search'> {
  constructor(init?: CustomEventInit) {
    super('search', init);
  }
}

export class SelectedDatesChangedEvent extends ShuiEvent<'selected-dates-changed'> {
  constructor(init?: CustomEventInit) {
    super('selected-dates-changed', init);
  }
}

export class ShowArrowEvent extends ShuiEvent<'showarrow'> {
  constructor(init?: CustomEventInit) {
    super('showarrow', init);
  }
}

export class SkipBackClickedEvent extends ShuiEvent<'skip-back-clicked'> {
  constructor(init?: CustomEventInit) {
    super('skip-back-clicked', init);
  }
}

export class SkipForwardClickedEvent extends ShuiEvent<'skip-forward-clicked'> {
  constructor(init?: CustomEventInit) {
    super('skip-forward-clicked', init);
  }
}

export class SlotsModifiedEvent extends ShuiEvent<'slots-modified'> {
  constructor(init?: CustomEventInit) {
    super('slots-modified', init);
  }
}

export class ToggleEvent extends ShuiEvent<'toggle'> {
  constructor(init?: CustomEventInit) {
    super('toggle', init);
  }
}

export class TimeClickedEvent extends ShuiEvent<'time-clicked'> {
  constructor(init?: CustomEventInit) {
    super('time-clicked', init);
  }
}

export class ValuesChangedEvent extends ShuiEvent<'values-changed'> {
  constructor(init?: CustomEventInit) {
    super('values-changed', init);
  }
}

export class WorklineAnimationEndedEvent extends ShuiEvent<'workline-animation-ended'> {
  constructor(init?: CustomEventInit) {
    super('workline-animation-ended', init);
  }
}

export class WorklineItemFloatChangedEvent extends ShuiEvent<'workline-item-float-changed'> {
  constructor(init?: CustomEventInit) {
    super('workline-item-float-changed', init);
  }
}

export class WorklineItemSizeChangedEvent extends ShuiEvent<'workline-item-size-changed'> {
  constructor(init?: CustomEventInit) {
    super('workline-item-size-changed', init);
  }
}

export class YearClickedEvent extends ShuiEvent<'year-clicked', { clickedYear: number }> {
  constructor(init?: CustomEventInit<{ clickedYear: number }>) {
    super('year-clicked', init);
  }
}

export class YearHeaderClickedEvent extends ShuiEvent<'year-header-clicked'> {
  constructor(init?: CustomEventInit) {
    super('year-header-clicked', init);
  }
}

export class OnSelectEvent<T = void> extends ShuiEvent<'on-select', T> {
  constructor(init?: CustomEventInit<T>) {
    super('on-select', init);
  }
}

export class ActionClickedEvent<T = void> extends ShuiEvent<'action-clicked', T> {
  constructor(init?: CustomEventInit<T>) {
    super('action-clicked', init);
  }
}

export class DismissedEvent<T = void> extends ShuiEvent<'dismissed', T> {
  constructor(init?: CustomEventInit<T>) {
    super('dismissed', init);
  }
}

export class ShownEvent extends ShuiEvent<'shown'> {
  constructor(init?: CustomEventInit) {
    super('shown', init);
  }
}

// Define the interface for the optional details object of a ChangeEndEvent
interface ChangeEndEventDetails {
  value?: number;
  lowerValue?: number;
  upperValue?: number;
}

export class ChangeEndEvent<
  Prop extends string,
  ChangeEndEventDetails = void,
> extends CustomEvent<ChangeEndEventDetails> {
  override readonly type!: `${Prop}`;

  constructor(prop: Prop, init?: CustomEventInit<ChangeEndEventDetails>) {
    super(`${prop}`, init);
  }
}

export class ControlValueChangeEndEvent extends ChangeEndEvent<
  'control-value-change-end',
  ChangeEndEventDetails
> {
  constructor(init?: CustomEventInit<ChangeEndEventDetails>) {
    super('control-value-change-end', init);
  }
}

export class RangeValueChangeEndEvent extends ChangeEndEvent<
  'range-value-change-end',
  ChangeEndEventDetails
> {
  constructor(init?: CustomEventInit<ChangeEndEventDetails>) {
    super('range-value-change-end', init);
  }
}

export class ValueChangeEndEvent extends ChangeEndEvent<'value-change-end', ChangeEndEventDetails> {
  constructor(init?: CustomEventInit<ChangeEndEventDetails>) {
    super('value-change-end', init);
  }
}

/**
 * Base event class for our legacy propXYZ-changed events
 */
export class LegacyPropertyChangedEvent<
  Prop extends string,
  TDetail = void,
> extends CustomEvent<TDetail> {
  override readonly type!: `${Prop}-changed`;

  constructor(prop: Prop, init?: CustomEventInit<TDetail>) {
    super(`${prop}-changed`, init);
  }
}
/**
 * All the legacy events ordered alphabetically
 * the event name gets post-fixed by -changed
 */
export class DisabledPropertyChangedEvent extends LegacyPropertyChangedEvent<'disabled', boolean> {
  constructor(init?: CustomEventInit<boolean>) {
    super('disabled', init);
  }
}

export class IconPropertyChangedEvent extends LegacyPropertyChangedEvent<'icon'> {
  constructor() {
    super('icon');
  }
}

export class LabelPropertyChangedEvent extends LegacyPropertyChangedEvent<'label'> {
  constructor() {
    super('label');
  }
}

export class SizePropertyChangedEvent extends LegacyPropertyChangedEvent<'size'> {
  constructor() {
    super('size');
  }
}

export class ColorPropertyChangedEvent extends LegacyPropertyChangedEvent<'color'> {
  constructor() {
    super('color');
  }
}

export class TooltipPropertyChangedEvent extends LegacyPropertyChangedEvent<'tooltip'> {
  constructor() {
    super('tooltip');
  }
}

export class HrefPropertyChangedEvent extends LegacyPropertyChangedEvent<'href'> {
  constructor() {
    super('href');
  }
}
export class DragAndDropPropertyChangedEvent extends LegacyPropertyChangedEvent<'drag-and-drop'> {
  constructor() {
    super('drag-and-drop');
  }
}
export class IconColorPropertyChangedEvent extends LegacyPropertyChangedEvent<'icon-color'> {
  constructor() {
    super('icon-color');
  }
}
export class HeaderPropertyChangedEvent extends LegacyPropertyChangedEvent<'header'> {
  constructor() {
    super('header');
  }
}
export class ButtonPropertyChangedEvent extends LegacyPropertyChangedEvent<'button'> {
  constructor() {
    super('button');
  }
}
export class BadgePropertyChangedEvent extends LegacyPropertyChangedEvent<'badge'> {
  constructor() {
    super('badge');
  }
}
export class TogglePropertyChangedEvent extends LegacyPropertyChangedEvent<'toggle'> {
  constructor() {
    super('toggle');
  }
}
export class StickyPropertyChangedEvent extends LegacyPropertyChangedEvent<'sticky'> {
  constructor() {
    super('sticky');
  }
}

export class TitlePropertyChangedEvent extends LegacyPropertyChangedEvent<'title'> {
  constructor() {
    super('title');
  }
}

export class VariationPropertyChangedEvent extends LegacyPropertyChangedEvent<'variation'> {
  constructor() {
    super('variation');
  }
}

export class TargetPropertyChangedEvent extends LegacyPropertyChangedEvent<'target'> {
  constructor() {
    super('target');
  }
}
export class NumberPropertyChangedEvent extends LegacyPropertyChangedEvent<'number'> {
  constructor() {
    super('number');
  }
}

export class VisiblePropertyChangedEvent extends LegacyPropertyChangedEvent<'visible'> {
  constructor() {
    super('visible');
  }
}
export class TypePropertyChangedEvent extends LegacyPropertyChangedEvent<'type'> {
  constructor() {
    super('type');
  }
}
export class ModalHeightPropertyChangedEvent extends LegacyPropertyChangedEvent<'modal-height'> {
  constructor() {
    super('modal-height');
  }
}

export class ModalWidthPropertyChangedEvent extends LegacyPropertyChangedEvent<'modal-width'> {
  constructor() {
    super('modal-width');
  }
}
export class PagesPropertyChangedEvent extends LegacyPropertyChangedEvent<'pages'> {
  constructor() {
    super('pages');
  }
}
export class CurrentPagePropertyChangedEvent extends LegacyPropertyChangedEvent<'current-page'> {
  constructor() {
    super('current-page');
  }
}
export class SpacingPropertyChangedEvent extends LegacyPropertyChangedEvent<'spacing'> {
  constructor() {
    super('spacing');
  }
}
export class VariantPropertyChangedEvent extends LegacyPropertyChangedEvent<'variant'> {
  constructor() {
    super('variant');
  }
}
export class VerticalPropertyChangedEvent extends LegacyPropertyChangedEvent<'vertical'> {
  constructor() {
    super('vertical');
  }
}
export class IndeterminatePropertyChangedEvent extends LegacyPropertyChangedEvent<'indeterminate'> {
  constructor() {
    super('indeterminate');
  }
}
export class NeutralPropertyChangedEvent extends LegacyPropertyChangedEvent<'neutral'> {
  constructor() {
    super('neutral');
  }
}
export class HintPropertyChangedEvent extends LegacyPropertyChangedEvent<'hint'> {
  constructor() {
    super('hint');
  }
}
export class ErrorMessagePropertyChangedEvent extends LegacyPropertyChangedEvent<'error-message'> {
  constructor() {
    super('error-message');
  }
}
export class MultiSelectPropertyChangedEvent extends LegacyPropertyChangedEvent<'multi-select'> {
  constructor() {
    super('multi-select');
  }
}
export class NoBorderPropertyChangedEvent extends LegacyPropertyChangedEvent<'no-border'> {
  constructor() {
    super('no-border');
  }
}

export class MandatoryPropertyChangedEvent extends LegacyPropertyChangedEvent<'mandatory'> {
  constructor() {
    super('mandatory');
  }
}
export class SuccessPropertyChangedEvent extends LegacyPropertyChangedEvent<'success'> {
  constructor() {
    super('success');
  }
}
export class SafetyPropertyChangedEvent extends LegacyPropertyChangedEvent<'safety'> {
  constructor() {
    super('safety');
  }
}
export class EditablePropertyChangedEvent extends LegacyPropertyChangedEvent<'editable'> {
  constructor() {
    super('editable');
  }
}

export class ValuePropertyChangedEvent extends LegacyPropertyChangedEvent<'value', number> {
  constructor(init?: CustomEventInit<number>) {
    super('value', init);
  }
}

export class MultilinePropertyChangedEvent extends LegacyPropertyChangedEvent<'multiline'> {
  constructor() {
    super('multiline');
  }
}

export class FieldsPropertyChangedEvent extends LegacyPropertyChangedEvent<'fields'> {
  constructor() {
    super('fields');
  }
}

export class SrcPropertyChangedEvent extends LegacyPropertyChangedEvent<'src'> {
  constructor() {
    super('src');
  }
}

export class PlacementPropertyChangedEvent extends LegacyPropertyChangedEvent<'placement'> {
  constructor() {
    super('placement');
  }
}
export class ErrorPropertyChangedEvent extends LegacyPropertyChangedEvent<'error'> {
  constructor() {
    super('error');
  }
}
export class ImagePropertyChangedEvent extends LegacyPropertyChangedEvent<'image'> {
  constructor() {
    super('image');
  }
}

export class LabelAlignmentPropertyChangedEvent extends LegacyPropertyChangedEvent<'label-alignment'> {
  constructor() {
    super('label-alignment');
  }
}

export class OverlayPropertyChangedEvent extends LegacyPropertyChangedEvent<'overlay'> {
  constructor() {
    super('overlay');
  }
}

export class PausedPropertyChangedEvent extends LegacyPropertyChangedEvent<'paused'> {
  constructor() {
    super('paused');
  }
}

export class RadialPropertyChangedEvent extends LegacyPropertyChangedEvent<'radial'> {
  constructor() {
    super('radial');
  }
}

export class StatusPropertyChangedEvent extends LegacyPropertyChangedEvent<'status'> {
  constructor() {
    super('status');
  }
}

export class InfoPropertyChangedEvent extends LegacyPropertyChangedEvent<'info'> {
  constructor() {
    super('info');
  }
}

export class ModalPropertyChangedEvent extends LegacyPropertyChangedEvent<'modal'> {
  constructor() {
    super('modal');
  }
}

export class ProgressPropertyChangedEvent extends LegacyPropertyChangedEvent<'progress'> {
  constructor() {
    super('progress');
  }
}

export class SubHeadlinePropertyChangedEvent extends LegacyPropertyChangedEvent<'sub-headline'> {
  constructor() {
    super('sub-headline');
  }
}

export class VersionPropertyChangedEvent extends LegacyPropertyChangedEvent<'version'> {
  constructor() {
    super('version');
  }
}
export class NoClearPropertyChangedEvent extends LegacyPropertyChangedEvent<'no-clear'> {
  constructor() {
    super('no-clear');
  }
}
export class ActivePropertyChangedEvent extends LegacyPropertyChangedEvent<'active'> {
  constructor() {
    super('active');
  }
}

export class CurrentPropertyChangedEvent extends LegacyPropertyChangedEvent<'current'> {
  constructor() {
    super('current');
  }
}

export class DecimalsPropertyChangedEvent extends LegacyPropertyChangedEvent<'decimals'> {
  constructor() {
    super('decimals');
  }
}

export class EuropeanNumberConventionPropertyChangedEvent extends LegacyPropertyChangedEvent<'european-number-convention'> {
  constructor() {
    super('european-number-convention');
  }
}
export class ReadonlyPropertyChangedEvent extends LegacyPropertyChangedEvent<'readonly'> {
  constructor() {
    super('readonly');
  }
}

export class MessageVisiblePropertyChangedEvent extends LegacyPropertyChangedEvent<'message-visible'> {
  constructor() {
    super('message-visible');
  }
}

export class ThumbnailPropertyChangedEvent extends LegacyPropertyChangedEvent<'thumbnail'> {
  constructor() {
    super('thumbnail');
  }
}
export class FlatPropertyChangedEvent extends LegacyPropertyChangedEvent<'flat'> {
  constructor() {
    super('flat');
  }
}

export class ExpandedPropertyChangedEvent extends LegacyPropertyChangedEvent<'expanded'> {
  constructor() {
    super('expanded');
  }
}

export class MessagePropertyChangedEvent extends LegacyPropertyChangedEvent<'message'> {
  constructor() {
    super('message');
  }
}

export class VisualFocusPropertyChangedEvent extends LegacyPropertyChangedEvent<'visual-focus'> {
  constructor() {
    super('visual-focus');
  }
}

export class SelectablePropertyChangedEvent extends LegacyPropertyChangedEvent<'selectable'> {
  constructor() {
    super('selectable');
  }
}

export class NewPropertyChangedEvent extends LegacyPropertyChangedEvent<'new'> {
  constructor() {
    super('new');
  }
}
export class SubMenuPropertyChangedEvent extends LegacyPropertyChangedEvent<'sub-menu'> {
  constructor() {
    super('sub-menu');
  }
}

export class PositionPropertyChangedEvent extends LegacyPropertyChangedEvent<'position'> {
  constructor() {
    super('position');
  }
}

export class PassivePropertyChangedEvent extends LegacyPropertyChangedEvent<'passive'> {
  constructor() {
    super('passive');
  }
}

export class MobilePropertyChangedEvent extends LegacyPropertyChangedEvent<'mobile'> {
  constructor() {
    super('mobile');
  }
}
export class MiniPropertyChangedEvent extends LegacyPropertyChangedEvent<'mini'> {
  constructor() {
    super('mini');
  }
}

export class CheckedPropertyChangedEvent extends LegacyPropertyChangedEvent<'checked'> {
  constructor() {
    super('checked');
  }
}

export class OptionsPropertyChangedEvent extends LegacyPropertyChangedEvent<'options'> {
  constructor() {
    super('options');
  }
}
export class CollapsedPropertyChangedEvent extends LegacyPropertyChangedEvent<'collapsed'> {
  constructor() {
    super('collapsed');
  }
}

export class CondensedPropertyChangedEvent extends LegacyPropertyChangedEvent<'condensed'> {
  constructor() {
    super('condensed');
  }
}

export class NativePropertyChangedEvent extends LegacyPropertyChangedEvent<'native'> {
  constructor() {
    super('native');
  }
}

export class EmbeddedPropertyChangedEvent extends LegacyPropertyChangedEvent<'embedded'> {
  constructor() {
    super('embedded');
  }
}
export class TextareaPropertyChangedEvent extends LegacyPropertyChangedEvent<'textarea'> {
  constructor() {
    super('textarea');
  }
}
export class ResizablePropertyChangedEvent extends LegacyPropertyChangedEvent<'resizable'> {
  constructor() {
    super('resizable');
  }
}
export class CardHeightPropertyChangedEvent extends LegacyPropertyChangedEvent<'card-height'> {
  constructor() {
    super('card-height');
  }
}
export class ArrowPositionPropertyChangedEvent extends LegacyPropertyChangedEvent<'arrow-position'> {
  constructor() {
    super('arrow-position');
  }
}

export class BehaviorPropertyChangedEvent extends LegacyPropertyChangedEvent<'behavior'> {
  constructor() {
    super('behavior');
  }
}
export class StretchPropertyChangedEvent extends LegacyPropertyChangedEvent<'stretch'> {
  constructor() {
    super('stretch');
  }
}
export class HPropertyChangedEvent extends LegacyPropertyChangedEvent<'h'> {
  constructor() {
    super('h');
  }
}

export class MPropertyChangedEvent extends LegacyPropertyChangedEvent<'m'> {
  constructor() {
    super('m');
  }
}

export class SPropertyChangedEvent extends LegacyPropertyChangedEvent<'s'> {
  constructor() {
    super('s');
  }
}

export class MeridiemPropertyChangedEvent extends LegacyPropertyChangedEvent<'am-pm'> {
  constructor() {
    super('am-pm');
  }
}

export class AmPmPropertyChangedEvent extends LegacyPropertyChangedEvent<'am-pm'> {
  constructor() {
    super('am-pm');
  }
}

export class StepPropertyChangedEvent extends LegacyPropertyChangedEvent<'step'> {
  constructor() {
    super('step');
  }
}

export class ClockFormatPropertyChangedEvent extends LegacyPropertyChangedEvent<'clock-format'> {
  constructor() {
    super('clock-format');
  }
}

export class ValPropertyChangedEvent extends LegacyPropertyChangedEvent<'val'> {
  constructor() {
    super('val');
  }
}

export class TimeValueChangedEvent extends LegacyPropertyChangedEvent<'time-value'> {
  constructor() {
    super('time-value');
  }
}

export class CurrentActivePropertyChangedEvent extends LegacyPropertyChangedEvent<'current-active'> {
  constructor() {
    super('current-active');
  }
}

export class CurrentSelectedPropertyChangedEvent extends LegacyPropertyChangedEvent<'current-selected'> {
  constructor() {
    super('current-selected');
  }
}

export class DrawerWidthPropertyChangedEvent extends LegacyPropertyChangedEvent<'drawer-width'> {
  constructor() {
    super('drawer-width');
  }
}
export class AspectRatioPropertyChangedEvent extends LegacyPropertyChangedEvent<'aspect-ratio'> {
  constructor() {
    super('aspect-ratio');
  }
}
export class MinPropertyChangedEvent extends LegacyPropertyChangedEvent<'min'> {
  constructor() {
    super('min');
  }
}

export class MaxPropertyChangedEvent extends LegacyPropertyChangedEvent<'max'> {
  constructor() {
    super('max');
  }
}

export class KeypadPropertyChangedEvent extends LegacyPropertyChangedEvent<'keypad'> {
  constructor() {
    super('keypad');
  }
}

export class NoArrowsPropertyChangedEvent extends LegacyPropertyChangedEvent<'no-arrows'> {
  constructor() {
    super('no-arrows');
  }
}

export class InputDisabledPropertyChangedEvent extends LegacyPropertyChangedEvent<'input-disabled'> {
  constructor() {
    super('input-disabled');
  }
}

export class KeypadActivePropertyChangedEvent extends LegacyPropertyChangedEvent<'keypad-active'> {
  constructor() {
    super('keypad-active');
  }
}

export class KeypadDisabledPropertyChangedEvent extends LegacyPropertyChangedEvent<'keypad-disabled'> {
  constructor() {
    super('keypad-disabled');
  }
}

export class NoValidatePropertyChangedEvent extends LegacyPropertyChangedEvent<'no-validate'> {
  constructor() {
    super('no-validate');
  }
}
export class FixedPropertyChangedEvent extends LegacyPropertyChangedEvent<'fixed'> {
  constructor() {
    super('fixed');
  }
}
export class InitialsPropertyChangedEvent extends LegacyPropertyChangedEvent<'initials'> {
  constructor() {
    super('initials');
  }
}

export class NamePropertyChangedEvent extends LegacyPropertyChangedEvent<'name'> {
  constructor() {
    super('name');
  }
}
export class PatientPropertyChangedEvent extends LegacyPropertyChangedEvent<'patient'> {
  constructor() {
    super('patient');
  }
}

export class ShowControlsPropertyChangedEvent extends LegacyPropertyChangedEvent<'show-controls'> {
  constructor() {
    super('show-controls');
  }
}

export class ShowRangePropertyChangedEvent extends LegacyPropertyChangedEvent<'show-range'> {
  constructor() {
    super('show-range');
  }
}

export class LowerValuePropertyChangedEvent extends LegacyPropertyChangedEvent<'lower-value'> {
  constructor(init?: CustomEventInit) {
    super('lower-value', init);
  }
}

export class UpperValuePropertyChangedEvent extends LegacyPropertyChangedEvent<'upper-value'> {
  constructor(init?: CustomEventInit) {
    super('upper-value', init);
  }
}

export class UnauthenticatedPropertyChangedEvent extends LegacyPropertyChangedEvent<'unauthenticated'> {
  constructor() {
    super('unauthenticated');
  }
}
export class BackgroundPropertyChangedEvent extends LegacyPropertyChangedEvent<'background'> {
  constructor() {
    super('background');
  }
}

export class PaddingPropertyChangedEvent extends LegacyPropertyChangedEvent<'padding'> {
  constructor() {
    super('padding');
  }
}

export class ElevationPropertyChangedEvent extends LegacyPropertyChangedEvent<'elevation'> {
  constructor() {
    super('elevation');
  }
}

export class ContentGapPropertyChangedEvent extends LegacyPropertyChangedEvent<'content-gap'> {
  constructor() {
    super('content-gap');
  }
}

export class LayoutPropertyChangedEvent extends LegacyPropertyChangedEvent<'layout'> {
  constructor() {
    super('layout');
  }
}

export class ActiveRegionsPropertyChangedEvent extends LegacyPropertyChangedEvent<'active-regions'> {
  constructor() {
    super('active-regions');
  }
}
